/**
 * TanStack Query hooks for the admin category-bindings surface — PIM Wave
 * P1 Task 8. Mirrors the `attribute-fields/hooks.ts` / `categories/hooks.ts`
 * idiom (query-key factory, invalidate-on-success mutations).
 *
 * `useCategoryBindings(categoryId)` and `useAncestorCategoryBindings(ids)`
 * share ONE query-options factory (`categoryBindingsQueryOptions`) — same
 * query key shape (`categoryBindingKeys.list`) and same fetcher
 * (`listCategoryBindings`) for the current category AND every ancestor
 * (brief §1: "ONE hook `useCategoryBindings(categoryId)` reused for both").
 * A literal single hook instance can't fan out over a DYNAMIC ancestor-id
 * array (rules of hooks forbid calling a hook a variable number of times in
 * a loop), so the ancestor fan-out goes through `useQueries` instead — but
 * it is built from the exact same options factory, so the "same shape"
 * requirement holds at the query-key/fetcher level, which is what the
 * shared `["admin","category-bindings",categoryId]` cache entries need to
 * line up correctly (an ancestor's bindings query and this category's own
 * query, if ever the same id, hit the same cache entry).
 *
 * Mutations (create/patch/delete/reorder) always target the CURRENT
 * category (brief §1) — ancestor bindings are read-only in this feature, no
 * mutation ever touches an ancestor id — so every mutation invalidates
 * `categoryBindingKeys.list(categoryId)` for the category passed in the
 * mutation variables. UPDATED (Task 10): every mutation ALSO invalidates
 * `formSchemaKeys.detail(categoryId)` — the server-side form-schema cache
 * invalidates itself independently (`service.invalidate_form_schemas`), but
 * the FE's own TanStack Query cache entry for `useFormSchema` does not know
 * that happened, so `FormPreview` (Task 10) would otherwise keep showing a
 * stale response until some unrelated refetch. This is the "additive edits
 * to the existing mutation hooks are in scope" the Task 10 brief calls for.
 *
 * `useFormSchema(categoryId)` is the FormPreview's OWN data source — it
 * fetches the PUBLIC `/marketplace/categories/{id}/form-schema` endpoint
 * directly (never the admin bindings data already loaded elsewhere on this
 * page), so the preview is zero-drift-by-construction with what the seller
 * form (P2) will render from the exact same contract.
 */
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  categoryBindingKeys,
  createCategoryBinding,
  deleteCategoryBinding,
  formSchemaKeys,
  getFormSchema,
  listCategoryBindings,
  reorderCategoryBindings,
  updateCategoryBinding,
  type CategoryBinding,
  type CategoryBindingCreate,
  type CategoryBindingListResponse,
  type CategoryBindingReorderBody,
  type CategoryBindingUpdate,
  type FormSchemaResponse,
} from "@/features/admin/category-bindings/api";

function categoryBindingsQueryOptions(categoryId: string): {
  queryKey: ReturnType<typeof categoryBindingKeys.list>;
  queryFn: () => Promise<CategoryBindingListResponse>;
  staleTime: number;
} {
  return {
    queryKey: categoryBindingKeys.list(categoryId),
    queryFn: () => listCategoryBindings(categoryId),
    staleTime: 10_000,
  };
}

export function useCategoryBindings(
  categoryId: string | null,
): UseQueryResult<CategoryBindingListResponse, Error> {
  return useQuery<CategoryBindingListResponse, Error>({
    ...categoryBindingsQueryOptions(categoryId ?? ""),
    enabled: categoryId !== null,
  });
}

/**
 * Parallel fan-out over the ancestor chain (nearest-first — the caller
 * decides ordering, this just preserves array order) — one query per
 * ancestor id, same key/fetcher shape as `useCategoryBindings`. Used by
 * `InheritedBindingsPanel` (USER RULING C3: no resolved-with-provenance
 * endpoint, so every ancestor's DIRECT bindings are fetched individually and
 * nearest-wins-resolved client-side).
 */
export function useAncestorCategoryBindings(
  ancestorIds: string[],
): UseQueryResult<CategoryBindingListResponse, Error>[] {
  return useQueries({
    queries: ancestorIds.map((ancestorId) => categoryBindingsQueryOptions(ancestorId)),
  }) as UseQueryResult<CategoryBindingListResponse, Error>[];
}

/** Invalidates BOTH this feature's own bindings cache AND the FormPreview's
 * form-schema cache for the same category (Task 10 — see file header). */
function useInvalidateCategoryBindings(): (categoryId: string) => Promise<void> {
  const qc = useQueryClient();
  return async (categoryId: string) => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: categoryBindingKeys.list(categoryId) }),
      qc.invalidateQueries({ queryKey: formSchemaKeys.detail(categoryId) }),
    ]);
  };
}

export function useCreateCategoryBinding(): UseMutationResult<
  CategoryBinding,
  Error,
  { categoryId: string; body: CategoryBindingCreate }
> {
  const invalidate = useInvalidateCategoryBindings();
  return useMutation<
    CategoryBinding,
    Error,
    { categoryId: string; body: CategoryBindingCreate }
  >({
    mutationFn: ({ categoryId, body }) => createCategoryBinding(categoryId, body),
    onSuccess: (_data, { categoryId }) => invalidate(categoryId),
  });
}

export function useUpdateCategoryBinding(): UseMutationResult<
  CategoryBinding,
  Error,
  { categoryId: string; bindingId: string; body: CategoryBindingUpdate }
> {
  const invalidate = useInvalidateCategoryBindings();
  return useMutation<
    CategoryBinding,
    Error,
    { categoryId: string; bindingId: string; body: CategoryBindingUpdate }
  >({
    mutationFn: ({ categoryId, bindingId, body }) =>
      updateCategoryBinding(categoryId, bindingId, body),
    onSuccess: (_data, { categoryId }) => invalidate(categoryId),
  });
}

export function useDeleteCategoryBinding(): UseMutationResult<
  void,
  Error,
  { categoryId: string; bindingId: string }
> {
  const invalidate = useInvalidateCategoryBindings();
  return useMutation<void, Error, { categoryId: string; bindingId: string }>({
    mutationFn: ({ categoryId, bindingId }) => deleteCategoryBinding(categoryId, bindingId),
    onSuccess: (_data, { categoryId }) => invalidate(categoryId),
  });
}

export function useReorderCategoryBindings(): UseMutationResult<
  CategoryBindingListResponse,
  Error,
  { categoryId: string; body: CategoryBindingReorderBody }
> {
  const invalidate = useInvalidateCategoryBindings();
  return useMutation<
    CategoryBindingListResponse,
    Error,
    { categoryId: string; body: CategoryBindingReorderBody }
  >({
    mutationFn: ({ categoryId, body }) => reorderCategoryBindings(categoryId, body),
    onSuccess: (_data, { categoryId }) => invalidate(categoryId),
  });
}

/**
 * FormPreview's (Task 10) sole data source — the PUBLIC, unauthenticated
 * form-schema read, query key `["marketplace","form-schema",categoryId]`
 * (brief §1 exact shape — a DIFFERENT top-level namespace than this
 * feature's own `["admin","category-bindings",...]` keys, since this is
 * shared with whatever the seller form (P2) eventually uses). `staleTime: 0`
 * mirrors the recon A2 "detail" idiom (`categories/hooks.ts`
 * `useCategory`-equivalent) — the preview should never mask a fresh
 * mutation behind a stale-time window; every mutation hook above already
 * invalidates this key on success, and the refresh button (FormPreview's
 * own chrome) forces an explicit refetch on demand.
 */
export function useFormSchema(categoryId: string | null): UseQueryResult<FormSchemaResponse, Error> {
  return useQuery<FormSchemaResponse, Error>({
    queryKey: formSchemaKeys.detail(categoryId ?? ""),
    queryFn: () => getFormSchema(categoryId ?? ""),
    enabled: categoryId !== null && categoryId !== "",
    staleTime: 0,
  });
}
